import { IncidentDto } from '@cradua/data';
import { createReducer, on, Action } from '@ngrx/store';
import { EntityState, EntityAdapter, createEntityAdapter } from '@ngrx/entity';

import * as IncidentActions from './incident.actions';

export const INCIDENT_FEATURE_KEY = 'incident';

export interface IncidentState
  extends EntityState<IncidentDto> {
  selectedIncidentId?: string | number; // which Incident record has been selected
  loaded: boolean; // has the Incident list been loaded
  error?: string | null; // last none error (if any)
}

export interface IncidentPartialState {
  readonly [INCIDENT_FEATURE_KEY]: IncidentState;
}

export const incidentAdapter: EntityAdapter<IncidentDto> = createEntityAdapter<
IncidentDto
>({
  selectId: (incident) => incident.id,
  sortComparer: sortByName,
});

export function sortByName(
  a: IncidentDto,
  b: IncidentDto
): number {
  return new Date(b.dateCreated).getTime() - new Date(a.dateCreated).getTime();
}

export const initialState: IncidentState = incidentAdapter.getInitialState(
  {
    // set initial required properties
    loaded: false,
    error: null,
    selectedIncidentId: null,
  }
);

const incidentReducer = createReducer(
  initialState,
  on(IncidentActions.loadIncident, (state) => ({
    ...state,
    loaded: true,
    error: null,
  })),
  on(
    IncidentActions.loadIncidentSuccess,
    (state, { incident }) =>
      incidentAdapter.setAll(incident, {
        ...state,
        loaded: false,
      })
  ),
  on(
    IncidentActions.loadIncidentFailure,
    (state, { error }) => ({
      ...state,
      loaded: false,
      error,
    })
  ),
  on(IncidentActions.addIncident, (state) => ({
    ...state,
    loaded: true,
    error: null,
  })),
  on(
    IncidentActions.addIncidentSuccess,
    (state, { newIncident }) =>
      incidentAdapter.addOne(newIncident, {
        ...state,
        loaded: false,
      })
  ),
  on(
    IncidentActions.addIncidentFailure,
    (state, { error }) => ({
      ...state,
      loaded: false,
      error,
    })
  ),
  on(IncidentActions.updateIncident, (state) => ({
    ...state,
    loaded: true,
    error: null,
  })),
  on(
    IncidentActions.updateIncidentSuccess,
    (state, { updateIncident }) =>
      incidentAdapter.upsertOne(updateIncident, {
        ...state,
        loaded: false,
      })
  ),
  on(
    IncidentActions.updateIncidentFailure,
    (state, { error }) => ({
      ...state,
      loaded: false,
      error,
    })
  ),

  //fetch incident record
  on(IncidentActions.fetchIncidentRecord, (state) => ({
    ...state,
    loaded: true,
    error: null,
  })),
  on(
    IncidentActions.fetchIncidentRecordSuccess,
    (state, { record }) =>
      incidentAdapter.setAll(record, {
        ...state,
        loaded: false,
      }),
  ),
  on(IncidentActions.fetchIncidentRecordFailure, (state, { error }) => ({
    ...state,
    loaded: false,
    error,
  })),


  //fetch incident record by id
  on(IncidentActions.fetchByIdIncidentRecord, (state) => ({
    ...state,
    loaded: true,
    error: null,
  })),
  on(
    IncidentActions.fetchByIdIncidentRecordSuccess,
    (state, { record }) =>
      incidentAdapter.setAll(record, {
        ...state,
        loaded: false,
      }),
  ),
  on(IncidentActions.fetchByIdIncidentRecordFailure, (state, { error }) => ({
    ...state,
    loaded: false,
    error,
  })),

  // search incident

  on(
    IncidentActions.searchIncidentRecord,
    (state, { query }) => {
      return {
        ...state,
        selectedIncidentId: query,
        loaded: false,
        error: null,
      };
    }
  ),

  on(
    IncidentActions.searchIncidentIdRecord,
    (state, { query }) => {
      return {
        ...state,
        selectedIncidentId: query,
        loaded: false,
        error: null,
      };
    }
  ),

  on(IncidentActions.removeIncident, (state) => ({
    ...state,
    loaded: true,
    error: null,
  })),
  on(
    IncidentActions.removeIncidentSuccess,
    (state, { id }) =>
      incidentAdapter.removeOne(id, { ...state, loaded: false })
  ),
  on(
    IncidentActions.removeIncidentFailure,
    (state, { error }) => ({
      ...state,
      loaded: false,
      error,
    })
  ),
  on(IncidentActions.clearIncident, (state) => ({
    ...state,
    loaded: true,
    error: null,
  })),
  on(
    IncidentActions.clearIncidentSuccess,
    (state, { Incident }) =>
    incidentAdapter.setAll(Incident, {
        ...state,
        loaded: false,
      }),
  ),
);

export function reducer(
  state: IncidentState | undefined,
  action: Action
) {
  return incidentReducer(state, action);
}
